import React, { useState, useEffect } from "react";
import { Modal } from "./Modal";
import allImgPaths from "assets/images";
import { InputTextField } from "components/InputField";
import { getExaminerCitationsDetails } from "services/project";

interface PatentNumberInputModalProps {
  isOpen: boolean;
  onClose: () => void;
  onPatentFound: (
    patentId: string,
    patentNumber: string,
    citationsData: any
  ) => void;
}

const PatentNumberInputModal: React.FC<PatentNumberInputModalProps> = ({
  isOpen,
  onClose,
  onPatentFound,
}) => {
  const [patentNumber, setPatentNumber] = useState("");
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    setPatentNumber("");
    setErrorMessage(null);
  }, [isOpen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;
    setPatentNumber(newValue);

    if (errorMessage) {
      setErrorMessage(null);
    }
  };

  const handleSearch = async () => {
    const normalizedPatentNumber = patentNumber.trim();

    if (!normalizedPatentNumber) {
      setErrorMessage("Please enter a valid patent number");
      return;
    }

    try {
      setErrorMessage(null);
      setIsLoading(true);

      const response = await getExaminerCitationsDetails(
        normalizedPatentNumber
      );

      if (response && response.data) {
        onClose();
        onPatentFound(
          normalizedPatentNumber,
          normalizedPatentNumber,
          response.data
        );
      } else {
        setErrorMessage("No examiner citations found for this patent");
      }
    } catch (error) {
      setErrorMessage(
        "Unable to retrieve examiner citations. Please verify the patent number."
      );
      console.error("Error fetching examiner citations:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Modal className="modal-wrapper max-w-[500px]" isShow={isOpen}>
      <div>
        <div className="modal-header pt-6 px-6">
          <div className="flex items-center justify-between pb-3">
            <img
              className="cursor-pointer"
              src={allImgPaths.addPatentLogo}
              alt="patent logo"
            />
            <img
              className="modal-close cursor-pointer hover:bg-[#f2f4f7] rounded w-5 p-1 mb-4"
              src={allImgPaths.closeIcon}
              onClick={onClose}
              alt="close icon"
            />
          </div>
          <h2 className="text-base text-[#0A090B] font-interSemiBold pb-1">
            Enter Patent Number
          </h2>
          <p className="text-sm text-[#4F4D55]">
            Please enter a valid patent or application number to retrieve
            examiner citations.
          </p>
        </div>

        <div className="modal-body px-6 pt-6">
          <div className="text-[#2D2B32] font-interMedium text-left pb-1.5 flex gap-1">
            Patent Number
            <img src={allImgPaths.questionCircle} alt="help" />
          </div>
          <div className="mb-4">
            <InputTextField
              name="patentNumber"
              value={patentNumber}
              onChange={handleInputChange}
              placeholder="Enter patent number"
              className="placeholder:text-[#7F7D83] placeholder:text-[15px] text-base text-[#344054] w-full"
              style={errorMessage ? "border-red-500" : ""}
              err={errorMessage || undefined}
            />
          </div>
        </div>

        <div className="modal-footer pt-8 px-6 pb-6">
          <div className="flex items-center gap-x-3 justify-end">
            <button
              type="button"
              onClick={onClose}
              className="text-[#344054] font-interSemiBold border border-[#D0D5DD] rounded-lg tracking-wide py-2.5 px-4 text-sm hover:bg-[#f2f4f7]"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSearch}
              disabled={isLoading || !patentNumber.trim()}
              className={`font-interSemiBold text-sm text-white py-2.5 px-4 border rounded-lg flex items-center justify-center ${
                isLoading || !patentNumber.trim()
                  ? "bg-[#9EBCF1] border-[#9EBCF1] cursor-not-allowed"
                  : "bg-[#1751D0] border-[#1751D0] hover:bg-[#113B98] cursor-pointer"
              }`}
            >
              {isLoading ? "Searching..." : "Search"}
            </button>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default PatentNumberInputModal;
